import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from '../../stores/redux';
import {
  getChatUserIdFromIdentity,
  getUniqueMembersByUserId,
  getMemberStatus as getMemberStatusHelper,
} from './realtime.helpers';
import {
  RealtimeRole,
  RealtimeMemberStatus,
  type RealtimeMember,
} from './realtime.types';

const getRealtimeState = (state: RootState) => state.realtime;

export const getState = (state: RootState) => getRealtimeState(state).state;

export const getIdentity = (state: RootState) =>
  getRealtimeState(state).identity;

/**
 * To verify if the user has already checked in or not.
 */
export const getIsCheckedIn = (state: RootState) => Boolean(getIdentity(state));

export const getIdentityId = (state: RootState) => getIdentity(state)?.id;

export const getRole = (state: RootState) => getIdentity(state)?.role;

export const getIsProvider = (state: RootState) => {
  return getRole(state) === RealtimeRole.Provider;
};

/**
 * Returns all stored members map, all members that were ever joined
 * Use it only if you do not care if member currently connected
 */
export const getAllMembersMap = (state: RootState) => state.realtime.membersMap;

/**
 * Returns currently joined members ids
 */
export const getJoinedMembersIds = (state: RootState) =>
  getRealtimeState(state).joinedMembersIds;

/**
 * Returns currently removed members ids
 */
export const getRemovedMembersIds = (state: RootState) =>
  getRealtimeState(state).removedMembersIds;

/**
 * Returns currently joined members ids excluding removed members
 */
export const getJoinedMembersIdsExcludingRemoved = createSelector(
  getJoinedMembersIds,
  getRemovedMembersIds,
  (joinedMembersIds, removedMembersIds) =>
    joinedMembersIds.filter((id) => !removedMembersIds.includes(id)),
);

/**
 * Returns currently all members array
 */
export const getAllMembers = createSelector(getAllMembersMap, (membersMap) =>
  Object.values(membersMap),
);

/**
 * Returns currently joined members map, use this im most cases
 *
 * NOTE: userId can be duplicated in multiple entries
 */
export const getNonUniqueMembersMap = createSelector(
  getAllMembersMap,
  getJoinedMembersIdsExcludingRemoved,
  (membersMap, joinedMembersIds) =>
    Object.fromEntries(
      Object.entries(membersMap).filter(([id]) =>
        joinedMembersIds.includes(id),
      ),
    ) as Record<string, RealtimeMember>,
);

export const getNonUniqueMembers = createSelector(
  getNonUniqueMembersMap,
  (membersMap) => Object.values(membersMap),
);

/**
 * Returns currently joined members but only the ones with unique useId
 *
 * The returned object will only contain first member with a certain userId
 * this is because it's possible to create a bug where the same user appears twice
 * this happens by either opening the same url in a different tab, or because of account sharing,
 * or simply because of realtime slowness in updating what users are actually connected.
 */
export const getMembersMap = createSelector(getNonUniqueMembersMap, (members) =>
  getUniqueMembersByUserId(members),
);

/**
 * Returns currently joined members array, use this im most cases
 */
export const getMembers = createSelector(getMembersMap, (membersMap) =>
  Object.values(membersMap),
);

/**
 * Non-offline selectors -----------------------------------------------------------
 * - getNonOfflineMembersMap
 * - getNonOfflineMembers
 * - getNonOfflineMemberIds
 *
 * These selectors exclude members whose status is explicitly set to Offline.
 * "Offline" here does not mean the user disconnected from realtime — it means
 * we intentionally marked them as Offline, e.g. a patient is set to Offline
 * while being transferred between rooms.
 *
 * Prefer these over getMembersMap / getMembers / getMemberIds in UI code
 * to avoid showing members there they are in the middle of a transfer.
 */
export const getNonOfflineMembersMap = createSelector(
  getMembersMap,
  (membersMap) =>
    Object.fromEntries(
      Object.entries(membersMap).filter(
        ([, member]) => member.state.status !== RealtimeMemberStatus.Offline,
      ),
    ) as Record<string, RealtimeMember>,
);

export const getNonOfflineMembers = createSelector(
  getNonOfflineMembersMap,
  (membersMap) => Object.values(membersMap),
);

export const getNonOfflineMemberIds = createSelector(
  getNonOfflineMembers,
  (members) => members.map((member) => member.identity.id),
);
/**
 * Non-offline selectors -----------------------------------------------------------
 */

/**
 * Returns array of ids of currently joined members
 */
export const getMemberIds = createSelector(getMembers, (members) =>
  members.map((member) => member.identity.id),
);

export const getMember = createSelector(
  getMembersMap,
  (state: RootState, identityId?: string): string | undefined => identityId,
  (membersMap, identityId) =>
    identityId !== undefined ? membersMap[identityId] : undefined,
);

/**
 * chatUserId is:
 *  - the userId: for providers
 *  - the realtimeId: for patients
 *
 * Note that realtimeId is always unique, but userId can be duplicated if the same
 * provider opens multiple tabs.
 */
export const getChatUserId = createSelector(getRealtimeState, (state) =>
  getChatUserIdFromIdentity(state.identity),
);

/**
 * Returns all realtime ids that have the same userId as the one provided
 *
 * NOTE: The condition that checks if the userId is defined is extremely important
 * because all patients will have undefined userId, so if we don't check for it
 * we might end up sending the same realtime message to all patients.
 */

export const getAllRealtimeIdsByChatUserId = createSelector(
  getNonUniqueMembers,
  (_state: RootState, chatUserId: string): string => chatUserId,
  (members, chatUserId) =>
    members
      .filter(
        (member) => getChatUserIdFromIdentity(member.identity) === chatUserId,
      )
      .map((member) => member.identity.id),
);

/**
 * Get member status by considering all existing members for the userId,
 * returns busy if at least one is busy, otherwise returns particular (for which the id was provided) member status
 */
export const getMemberStatus = createSelector(
  getNonUniqueMembersMap,
  getMember,
  getMemberStatusHelper,
);

export const getUserState = (state: RootState) => state.realtime.state;

export const getUserPicture = (state: RootState) => state.realtime.picture;

/**
 * @deprecated This should not be used, use useGetUserQuery RTK query instead
 */
export const getUsername = (state: RootState) => state.realtime.state?.username;

export const getLegacyIdForMember = createSelector(
  getAllMembersMap,
  (_: RootState, memberId: string) => memberId,
  (members, memberId: string) => members[memberId]?.state.legacyIdentityId,
);

export const getProviderName = createSelector(getMembersMap, (members) => {
  const provider = Object.values(members).find(
    (member) => member.identity.role === RealtimeRole.Provider,
  );

  return provider?.state['username'];
});

/**
 * Returns realtime members in call based on Busy status.
 */
export const getMembersInCall = createSelector(getMembers, (members) =>
  members.filter((member) => member.state.status === RealtimeMemberStatus.Busy),
);

/**
 * Returns true if there are patients in the queue (Online status)
 */
export const getIsPatientsQueuePopulated = createSelector(
  getJoinedMembersIdsExcludingRemoved,
  getAllMembersMap,
  (joinedMembersIds, membersMap) =>
    joinedMembersIds.some(
      (id) => membersMap[id]?.state.status === RealtimeMemberStatus.Online,
    ),
);
